import { getLinkIdOfQuestionnaireQuestion } from './questionnaire-engine.utils'

import type { IQuestionnaireQuestion } from '@healthblocks-io/core'

/**
 * Clamp a cursor to a history length
 * @param cursor - The cursor to clamp
 * @param historyLength - The length of the history
 * @returns The clamped cursor
 */
export function clampNavigationHistoryCursor(cursor: number, historyLength: number): number {
  if (historyLength <= 0) return 0
  return Math.max(0, Math.min(cursor, historyLength - 1))
}

/**
 * Truncate the future navigation history
 * @param history - The history to truncate
 * @param cursor - The cursor to truncate the history at
 * @returns The truncated history
 */
function truncateFutureNavigationHistory(history: string[], cursor: number): string[] {
  if (history.length === 0) return history
  const end = clampNavigationHistoryCursor(cursor, history.length) + 1
  return history.slice(0, end)
}

/**
 * Push a linkId to the navigation history
 * @param history - The history to push the linkId to
 * @param cursor - The cursor to push the linkId at
 * @param linkId - The linkId to push to the navigation history
 * @returns The new history and cursor
 */
export function pushLinkIdToNavigationHistory(history: string[], cursor: number, linkId: string) {
  const visited = truncateFutureNavigationHistory(history, cursor)
  const last = visited.at(-1)

  // avoid duplicates
  if (last === linkId) {
    return { history: visited, cursor: visited.length - 1 }
  }

  const next = [...visited, linkId]
  return { history: next, cursor: next.length - 1 }
}

/**
 * Replace a linkId in the navigation history
 * @param history - The history to replace the linkId in
 * @param cursor - The cursor to replace the linkId at
 * @param linkId - The linkId to replace in the navigation history
 * @returns The new history and cursor
 */
export function replaceLinkIdInNavigationHistory(history: string[], cursor: number, linkId: string) {
  if (history.length === 0) {
    return { history: [linkId], cursor: 0 }
  }

  const visited = truncateFutureNavigationHistory(history, cursor)
  const index = clampNavigationHistoryCursor(cursor, visited.length)

  const next = [...visited]
  next[index] = linkId

  // optional: collapse immediate duplicate like [Q1, Q2, Q2]
  if (index > 0 && next[index - 1] === next[index]) {
    next.splice(index, 1)
    return { history: next, cursor: index - 1 }
  }

  return { history: next, cursor: index }
}

/**
 * Clamp the navigation history to the answerable questions
 * @param history - The history to clamp
 * @param cursor - The cursor to clamp the history at
 * @param answerableQuestions - The answerable questions to clamp the history to
 * @returns The clamped history and cursor
 */
export function clampHistoryToAnswerable(history: string[], cursor: number, answerableQuestions: any[]) {
  const allowed = new Set(answerableQuestions.map(getLinkIdOfQuestionnaireQuestion))
  const safeCursor = clampNavigationHistoryCursor(cursor, history.length)
  const currentLinkId = history[safeCursor] ?? null

  const filtered = history.filter((id) => allowed.has(id))

  if (filtered.length === 0) {
    return { history: [], cursor: 0 }
  }

  if (!currentLinkId) {
    return { history: filtered, cursor: filtered.length - 1 }
  }

  const index = filtered.indexOf(currentLinkId)
  return {
    history: filtered,
    cursor: index === -1 ? filtered.length - 1 : index,
  }
}

/**
 * Build initial navigation history for non-reconfirm mode
 * Populates history with all answered questions + the next unanswered question
 * Positions cursor at the next unanswered question (end of history)
 *
 * @param answerableQuestions - All answerable questions in order
 * @param answers - Current answers map
 * @param initialLinkId - Optional initial linkId to navigate to
 * @returns Navigation history and cursor position
 */
export function buildNavigationHistory(
  answerableQuestions: IQuestionnaireQuestion[],
  answers: Record<string, unknown>,
  initialLinkId?: string,
): {
  navigationHistory: string[]
  historyCursor: number
} {
  const allLinkIds = answerableQuestions.map(getLinkIdOfQuestionnaireQuestion)

  // Step 1: Collect all answered questions (including Display questions with answer entries)
  const answeredLinkIds: string[] = []
  for (const question of answerableQuestions) {
    const linkId = getLinkIdOfQuestionnaireQuestion(question)
    const isDisplayQuestion = question.type === 'Display'

    if (isDisplayQuestion) {
      // Display questions: if they have an answer entry, they were "seen"
      if (linkId in answers) {
        answeredLinkIds.push(linkId)
      }
    } else {
      // Non-Display questions: check if they have a non-null answer
      const isAnswered = linkId in answers && answers[linkId] != null
      if (isAnswered) {
        answeredLinkIds.push(linkId)
      }
    }
  }

  // Step 2: Find the first unanswered question
  // If we have answered questions, only consider non-Display questions (skip Display)
  // If we have no answered questions, we can start at a Display question
  const hasAnsweredQuestions = answeredLinkIds.length > 0
  let firstUnansweredIndex = -1

  for (const [index, question] of answerableQuestions.entries()) {
    const linkId = getLinkIdOfQuestionnaireQuestion(question)
    const isDisplayQuestion = question.type === 'Display'

    // Skip if already answered
    if (answeredLinkIds.includes(linkId)) {
      continue
    }

    // If we have answered questions, skip Display questions
    if (hasAnsweredQuestions && isDisplayQuestion) {
      continue
    }

    // Found first unanswered question (non-Display, or Display if no answered questions)
    firstUnansweredIndex = index
    break
  }

  // Determine active linkId (next unanswered question)
  let activeLinkId: string
  if (initialLinkId && allLinkIds.includes(initialLinkId)) {
    activeLinkId = initialLinkId
  } else if (firstUnansweredIndex === -1) {
    // All questions are answered, start at the last one
    activeLinkId = allLinkIds.at(-1) ?? allLinkIds[0]
  } else {
    // Start at the first unanswered question
    activeLinkId = getLinkIdOfQuestionnaireQuestion(answerableQuestions[firstUnansweredIndex])
  }

  // Build history: populate with answered questions + the next unanswered question
  // History is forward (oldest to newest): [q1, q2, q3, ...]
  // This allows navigation back through answered questions and shows the current question
  const navigationHistory = answeredLinkIds.length > 0 ? [...answeredLinkIds, activeLinkId] : [activeLinkId]

  // Cursor points to the next unanswered question (the active question at the end of history)
  // If history is [q1, q2, q3] and cursor is 2, it points to q3 (index 2)
  const historyCursor = navigationHistory.length - 1

  return {
    navigationHistory,
    historyCursor,
  }
}

/**
 * Build initial navigation history for reconfirm mode
 * Initializes with a single question: initialLinkId or first invalid question
 * History grows as user navigates (each navigation pushes a new linkId)
 *
 * @param answerableQuestions - All answerable questions in order
 * @param validations - Validation state for each question
 * @param initialLinkId - Optional linkId to start at (if provided, must be in answerableQuestions)
 * @returns Navigation history and cursor position
 */
export function buildReconfirmNavigationHistory(
  answerableQuestions: IQuestionnaireQuestion[],
  validations: Record<string, { isValid: boolean }>,
  initialLinkId?: string,
): {
  navigationHistory: string[]
  historyCursor: number
} {
  const allLinkIds = answerableQuestions.map(getLinkIdOfQuestionnaireQuestion)

  // If initialLinkId is provided, use it (if valid)
  if (initialLinkId && allLinkIds.includes(initialLinkId)) {
    return {
      navigationHistory: [initialLinkId],
      historyCursor: 0,
    }
  }

  // Otherwise, find first invalid question
  for (const question of answerableQuestions) {
    const linkId = getLinkIdOfQuestionnaireQuestion(question)

    // Skip Display questions (they don't have validations)
    if (question.type === 'Display') continue

    const isValid = validations[linkId]?.isValid ?? false
    if (!isValid) {
      return {
        navigationHistory: [linkId],
        historyCursor: 0,
      }
    }
  }

  // If no invalid question found, use first question
  return {
    navigationHistory: [allLinkIds[0]],
    historyCursor: 0,
  }
}
